/**
 * 찬양팀 소개 페이지
 */

import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { MainLayout } from '../../../../shared/components';
import { theme } from '../../../../shared/styles/theme';
import { mediaQueries } from '../../../../shared/constants/breakpoints';
// 이미지 import - assets/images 폴더에 이미지 추가 후 주석 해제
import praiseTeam1 from '../../../../assets/images/praise_team_1.JPG';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.xxxl} ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  font-family: ${theme.fonts.heading};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  max-width: 800px;
  height: 400px;
  margin: 0 auto ${theme.spacing.xl};
  position: relative;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    height: 250px;
  }
`;

const TeamImage = styled.img<{ isActive: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => props.isActive ? 1 : 0};
  transition: opacity 1s ease-in-out;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.lg};
`;

const Section = styled.section`
  margin-bottom: ${theme.spacing.xxxl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xxl};
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.fonts.heading};
  border-bottom: 3px solid ${theme.colors.primary};
  padding-bottom: ${theme.spacing.md};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.xl};
  }
`;

const ContentText = styled.p`
  font-size: ${theme.fontSizes.lg};
  line-height: 1.8;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.md};
  }
`;

const VisionBox = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  padding: ${theme.spacing.xxl};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  box-shadow: ${theme.shadows.md};

  ${mediaQueries.maxMobile} {
    padding: ${theme.spacing.xl} ${theme.spacing.lg};
  }
`;

const VisionText = styled.p`
  font-size: ${theme.fontSizes.xl};
  line-height: 1.8;
  margin: 0;

  ${mediaQueries.maxMobile} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const InfoCard = styled.div`
  background-color: ${theme.colors.hover};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
`;

const InfoLabel = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.sm};
  font-weight: 600;
`;

const InfoValue = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.primary};
  font-weight: 700;
  margin: 0;
`;

export const PraiseTeam: React.FC = () => {
  const images = [praiseTeam1];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 4000); // 4초마다 이미지 변경

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <MainLayout>
      <PageContainer>
        <PageTitle>찬양팀</PageTitle>

        <SliderContainer>
          {images.length > 0 ? (
            images.map((image, index) => (
              <TeamImage
                key={index}
                src={image}
                alt={`찬양팀 ${index + 1}`}
                isActive={index === currentImageIndex}
              />
            ))
          ) : (
            <ImagePlaceholder>이미지를 추가해주세요</ImagePlaceholder>
          )}
        </SliderContainer>

        <Section>
          <SectionTitle>비전</SectionTitle>
          <VisionBox>
            <VisionText>
              현대적인 찬양으로 성도들의 예배를 인도하고, 하나님께 영광을 돌립니다.
            </VisionText>
          </VisionBox>
        </Section>

        <Section>
          <SectionTitle>소개</SectionTitle>
          <ContentText>
            찬양팀은 밴드 형태의 현대 찬양 팀으로,
            주일 예배와 각종 집회에서 찬양 인도를 담당하고 있습니다.
            악기 연주와 보컬이 조화를 이루어 은혜로운 찬양으로 하나님을 섬기고 있습니다.
          </ContentText>
        </Section>

        <Section>
          <SectionTitle>활동 시간</SectionTitle>
          <InfoCard>
            <InfoLabel>연습 시간</InfoLabel>
            <InfoValue>매주 토요일 오후 7:00</InfoValue>
          </InfoCard>
        </Section>
      </PageContainer>
    </MainLayout>
  );
};
